export type PipelineStage =
  | "new"
  | "review"
  | "pursue"
  | "drafted"
  | "approved"
  | "mailed"
  | "retained"
  | "closed";

/** Mirrors HUMAN_ALLOWED_TRANSITIONS from @newark/desk-core (kept local to avoid bundling pdf-lib). */
export const HUMAN_STAGE_TRANSITIONS: Record<PipelineStage, PipelineStage[]> = {
  new: ["review", "closed"],
  review: ["pursue", "closed"],
  pursue: ["drafted", "closed"],
  drafted: ["approved", "closed"],
  approved: ["mailed", "closed"],
  mailed: ["retained", "closed"],
  retained: ["closed"],
  closed: [],
};

export type DeskId =
  | "patent"
  | "ada"
  | "coverage"
  | "injury"
  | "workplace"
  | "maritime"
  | "trucking"
  | "aviation"
  | "cases";

export interface Workup {
  tenant_id: string;
  desk: string;
  item_id: string;
  stage: PipelineStage;
  close_reason: string | null;
  mail_name: string | null;
  mail_address: string | null;
  notes: string | null;
  score: number | null;
  rule_version: string | null;
  do_not_contact: number;
  updated_at: string;
}

export interface QueueItem {
  id: string | number;
  docketNumber?: string | null;
  caseName?: string | null;
  court?: string | null;
  courtId?: string | null;
  state?: string | null;
  dateFiled?: string | null;
  defendants?: string[];
  plaintiffs?: string[];
  signals?: string[];
  docketUrl?: string | null;
  workup: Workup | null;
  score: number;
  rule_version: string;
  hasMailAddress: boolean;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
  }
  if (res.headers.get("Content-Type")?.includes("application/json")) {
    return res.json() as Promise<T>;
  }
  return undefined as T;
}

export const api = {
  me: () => request<{ authenticated: boolean }>("/api/me"),
  login: (password: string) =>
    request<{ ok: boolean }>("/api/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/logout", { method: "POST" }),
  queue: (desk: DeskId, showClosed: boolean) =>
    request<{ desk: string; count: number; results: QueueItem[] }>(
      `/api/queue/${desk}${showClosed ? "?show_closed=1" : ""}`,
    ),
  setStage: (desk: DeskId, itemId: string, stage: PipelineStage, close_reason?: string) =>
    request<Workup>("/api/stage", {
      method: "POST",
      body: JSON.stringify({ desk, itemId, stage, close_reason }),
    }),
  importWorkups: (desk: DeskId, workups: Record<string, Record<string, unknown>>) =>
    request<{ imported: number; skipped: number }>("/api/workups/import", {
      method: "POST",
      body: JSON.stringify({ desk, workups }),
    }),
  exportCsvUrl: (desk: DeskId) => `/api/workups/export/${desk}`,
  packetPdfUrl: (date: string) => `/api/letters/packet?date=${date}`,
  packetCsvUrl: (date: string) => `/api/letters/packet?date=${date}&format=csv`,
};

export const DESKS: { id: DeskId; label: string }[] = [
  { id: "patent", label: "Patent" },
  { id: "ada", label: "ADA" },
  { id: "coverage", label: "Coverage" },
  { id: "injury", label: "Injury" },
  { id: "workplace", label: "Workplace" },
  { id: "maritime", label: "Maritime" },
  { id: "trucking", label: "Trucking" },
  { id: "aviation", label: "Aviation" },
  // `cases` omitted — residual classifier only (no CL mail queue)
];

export const LOCAL_WORKUP_KEY = "nf_workup";
